/**
 * frontend/src/services/checkoutService.js
 *
 * Wraps checkout payment-related backend endpoints.
 */

import { request, ROUTES } from './api';

// ─────────────────────────────────────────────────────────────
// Process checkout payment for hosted checkout or shopper wallet
// ─────────────────────────────────────────────────────────────

/**
 * @param {object} opts
 * @param {number} opts.addressId
 * @param {number[]} [opts.itemIds]
 * @param {'standard'|'express'} [opts.shippingMethod]
 * @param {Array<{businessId:number,shippingMethod:'standard'|'express',note:string}>} [opts.sellerOrders]
 * @param {string} [opts.clientRequestId]
 * @param {'PAYMONGO_CHECKOUT'|'SHOPPER_WALLET'} [opts.paymentMethod]
 * @param {'checkout'|'order_settlement'} [opts.returnFlow]
 * @returns {Promise<{ orderId:number, checkoutUrl:string|null, paymentStatus:string, paymentMethod:string|null, checkoutSessionId:string|null, paymentId:number, paidAt:string|null }>}
 */
export async function createSession({
    addressId,
    itemIds = [],
    shippingMethod = 'standard',
    sellerOrders = [],
    clientRequestId,
    paymentMethod = 'PAYMONGO_CHECKOUT',
    returnFlow,
}) {
    const res = await request({
        url: '/api/checkout/sessions/create-session',
        method: 'POST',
        data: {
            addressId,
            itemIds,
            shippingMethod,
            sellerOrders,
            clientRequestId,
            paymentMethod,
            ...(returnFlow ? { returnFlow } : {}),
        },
    });
    return res?.data ?? res;
}

/**
 * Can these sellers deliver to a saved address? Answered before the cart, so a
 * shopper never discovers a store is out of range at the payment step.
 *
 * Read-only and approximate by design: the server uses straight-line distance,
 * so `estimatedFeeFrom` is a floor, not a price. The exact fee is confirmed at
 * checkout against the real road route.
 *
 * @param {object} opts
 * @param {number} opts.addressId
 * @param {number[]} opts.businessIds
 * @returns {Promise<{ addressId:number|null, estimates:Array<{ businessId:number,
 *   status:'AVAILABLE'|'OUT_OF_AREA'|'ADDRESS_PIN_REQUIRED'|'STORE_NOT_READY',
 *   serviceRadiusKm?:number, straightLineKm?:number, estimatedFeeFrom?:string, currency?:string }> }>}
 */
export async function getDeliveryAvailability({ addressId, businessIds = [] }) {
    const res = await request({
        url: '/api/checkout/delivery-availability',
        method: 'GET',
        params: { addressId, businessIds: businessIds.join(',') },
    });
    return res?.data ?? res;
}

export async function getDeliveryQuotes({ addressId, itemIds = [] }) {
    const res = await request({
        url: '/api/checkout/delivery-quotes',
        method: 'POST',
        data: { addressId, itemIds },
    });
    return res?.data ?? res;
}

/**
 * @param {object} opts
 * @param {number} opts.orderId
 * @param {string} opts.clientRequestId
 * @param {number} [opts.addressId]
 * @param {'standard'|'express'} [opts.shippingMethod]
 * @param {Array<{businessId:number,shippingMethod:'standard'|'express',note:string}>} [opts.sellerOrders]
 * @param {'COD'|'PAYMONGO_CHECKOUT'|'SHOPPER_WALLET'} [opts.paymentMethod]
 * @param {'checkout'|'order_settlement'} [opts.returnFlow]
 * @returns {Promise<{ orderId:number, checkoutUrl:string|null, paymentStatus:string, paymentMethod:string|null, checkoutSessionId:string|null, paymentId:number, paidAt:string|null }>}
 */
export async function recoverSession({
    orderId,
    clientRequestId,
    addressId,
    shippingMethod,
    sellerOrders,
    paymentMethod,
    returnFlow,
}) {
    try {
        const res = await request({
            url: '/api/checkout/sessions/recover-session',
            method: 'POST',
            data: {
                orderId,
                clientRequestId,
                ...(addressId != null ? { addressId } : {}),
                ...(shippingMethod ? { shippingMethod } : {}),
                ...(Array.isArray(sellerOrders) ? { sellerOrders } : {}),
                ...(paymentMethod ? { paymentMethod } : {}),
                ...(returnFlow ? { returnFlow } : {}),
            },
        });
        return res?.data ?? res;
    } catch (error) {
        const status = Number(error?.status || 0);
        const message = String(error?.message || '');
        const hasRevision = addressId != null
            || Boolean(shippingMethod)
            || Array.isArray(sellerOrders)
            || Boolean(paymentMethod);
        const shouldFallback = !hasRevision && (status === 404 || /cannot post/i.test(message));

        if (!shouldFallback || !orderId) {
            throw error;
        }

        const fallback = await request({
            url: `${ROUTES.orders}/${orderId}/settle-payment`,
            method: 'POST',
            data: {
                clientRequestId,
                allowSessionRenewal: true,
                ...(returnFlow ? { returnFlow } : {}),
            },
        });

        return fallback?.data ?? fallback;
    }
}

/**
 * Redirect user to external payment/auth URL.
 */
export function redirectTo3DS(url) {
    if (url) window.location.href = url;
}
